import { useState, useEffect, useRef, useCallback } from 'react';

const CARD_COLORS = [
  '#4A90D9', '#4CAF82', '#E8734A', '#9B6DD4',
  '#F5C842', '#E84393', '#00BCD4', '#FF5722',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function deepCopy(obj) { return JSON.parse(JSON.stringify(obj)); }

function typeLabel(type) {
  return { constraint: 'constraint', emotional: 'emotional', external: 'external', waiting: 'waiting for' }[type] || type;
}

// ─── Shared layout ────────────────────────────────────────────────────────────

function Page({ children, onLogout }) {
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 24px',
      background: 'var(--bg)',
      boxSizing: 'border-box',
      position: 'relative',
    }}>
      {onLogout && <LogoutButton onLogout={onLogout} floating />}
      <div style={{ maxWidth: 640, width: '100%' }}>
        {children}
      </div>
    </div>
  );
}

// Shared "Log out" control so a user can leave onboarding at any step. `floating` pins it
// to the top-right (used by the centered Page steps); the brain-dump step renders it inline
// in its own header. Same icon/label everywhere for consistency.
function LogoutButton({ onLogout, floating }) {
  return (
    <button onClick={onLogout} style={{
      display: 'flex', alignItems: 'center', gap: 6,
      background: 'none', border: 'none', cursor: 'pointer',
      color: 'var(--text-2)', fontSize: 14, fontFamily: 'var(--font-body)',
      padding: '4px 0',
      // Pinned top-LEFT: the Feedback pill is fixed top-right (z-index 200) and would
      // otherwise cover a top-right logout on every onboarding step.
      ...(floating ? { position: 'absolute', top: 16, left: 24, zIndex: 201 } : {}),
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
        <polyline points="16 17 21 12 16 7"/>
        <line x1="21" y1="12" x2="9" y2="12"/>
      </svg>
      Log out
    </button>
  );
}

function H1({ children }) {
  return (
    <h1 style={{
      fontFamily: 'var(--font-display)',
      fontSize: 30,
      fontWeight: 400,
      lineHeight: 1.25,
      margin: '0 0 12px',
      color: 'var(--text)',
    }}>
      {children}
    </h1>
  );
}

function Sub({ children }) {
  return (
    <p style={{
      fontFamily: 'var(--font-body)',
      fontSize: 15,
      color: 'var(--text-2)',
      margin: '0 0 28px',
      lineHeight: 1.6,
    }}>
      {children}
    </p>
  );
}

function PrimaryButton({ onClick, disabled, loading, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        marginTop: 24,
        padding: '12px 28px',
        background: disabled || loading ? 'var(--text-3)' : 'var(--text)',
        color: 'var(--bg)',
        border: 'none',
        borderRadius: 8,
        fontSize: 15,
        fontFamily: 'var(--font-body)',
        fontWeight: 500,
        cursor: disabled || loading ? 'default' : 'pointer',
        transition: 'background .15s',
      }}
    >
      {loading ? 'Working…' : children}
    </button>
  );
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <p style={{
      marginTop: 12,
      color: 'var(--error)',
      fontSize: 13,
      fontFamily: 'var(--font-body)',
    }}>
      {msg}
    </p>
  );
}

// ─── Step 1 textarea ─────────────────────────────────────────────────────────

function BigTextarea({ value, onChange, placeholder, rows = 8, readOnly = false }) {
  return (
    <textarea
      autoFocus={!readOnly}
      value={value}
      onChange={e => onChange && onChange(e.target.value)}
      readOnly={readOnly}
      rows={rows}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '16px',
        border: '1px solid var(--border)',
        borderRadius: 8,
        fontSize: 15,
        fontFamily: 'var(--font-body)',
        lineHeight: 1.6,
        background: readOnly ? 'var(--surface)' : 'var(--surface)',
        color: readOnly ? 'var(--text-2)' : 'var(--text)',
        resize: 'vertical',
        boxSizing: 'border-box',
        outline: 'none',
        opacity: readOnly ? 0.7 : 1,
      }}
    />
  );
}

// ─── Brain dump screen (Screen 1) ─────────────────────────────────────────────

function MicIcon({ active }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke={active ? '#DC2626' : 'currentColor'}
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
      <line x1="12" y1="19" x2="12" y2="23"/>
      <line x1="8" y1="23" x2="16" y2="23"/>
    </svg>
  );
}

function BrainDumpScreen({ brainDump, setBrainDump, loading, error, onContinue, onLogout }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const speechSupported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const toggleDictation = useCallback(() => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onresult = (e) => {
      const transcript = Array.from(e.results)
        .slice(e.resultIndex)
        .filter(r => r.isFinal)
        .map(r => r[0].transcript.trim())
        .join(' ');
      if (transcript) setBrainDump(prev => prev ? prev + ' ' + transcript : transcript);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognition.start();
    recognitionRef.current = recognition;
    setListening(true);
  }, [listening, setBrainDump]);

  useEffect(() => () => recognitionRef.current?.stop(), []);

  const len = brainDump.trim().length;
  const hint = len === 0
    ? 'A few sentences is plenty'
    : len < 100
      ? 'Keep going — a bit more sharpens the read'
      : 'Looking good';
  const canContinue = len >= 20 && !loading;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      {/* App header */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 28px', height: 52, flexShrink: 0,
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
      }}>
        {/* Brand + logout grouped on the LEFT so the fixed top-right Feedback pill never
            covers the logout (the right slot is left empty for that pill). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 17,
            color: 'var(--text)', letterSpacing: '-0.01em',
          }}>
            Bearing360
          </span>
          <LogoutButton onLogout={onLogout} />
        </div>
      </header>

      {/* Content */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '48px 24px', boxSizing: 'border-box',
      }}>
        <div style={{ maxWidth: 560, width: '100%' }}>
          {/* Step marker — journey label, not a numbered step */}
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-3)',
            margin: '0 0 20px', letterSpacing: '0.02em',
          }}>
            <span style={{ color: 'var(--text)', fontWeight: 500 }}>Capture</span> → Understand → Reflect
          </p>

          {/* Heading */}
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 400,
            lineHeight: 1.2, margin: '0 0 12px', color: 'var(--text)',
          }}>
            What's competing for your attention right now?
          </h1>

          {/* Subcopy — explains why, then the payoff */}
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text-2)',
            margin: '0 0 8px', lineHeight: 1.6,
          }}>
            Whatever's pulling at you, work, family, life admin, put it here without organizing it
            first. This is how Bearing360 learns what actually matters to you, so it can hold the
            picture instead of you having to. Type it, or hit the mic and just talk.
          </p>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-3)',
            margin: '0 0 24px', lineHeight: 1.6,
          }}>
            In about two minutes, you'll get a structured picture of what's competing for your attention.
          </p>

          {/* Input container */}
          <div style={{
            border: '1px solid var(--border)', borderRadius: 10,
            background: 'var(--surface)', overflow: 'hidden',
          }}>
            <textarea
              autoFocus
              value={brainDump}
              onChange={e => setBrainDump(e.target.value)}
              placeholder="ugh ok. work's a mess, three deadlines this week and I haven't started the deck. also need to sort the lease before it auto-renews. and I really need more sleep, been running on 5 hours all week"
              style={{
                display: 'block', width: '100%', boxSizing: 'border-box',
                padding: '16px 18px', minHeight: 150,
                border: 'none', outline: 'none', resize: 'none',
                fontFamily: 'var(--font-body)', fontSize: 15, lineHeight: 1.6,
                color: 'var(--text)', background: 'transparent',
              }}
            />

            {/* Footer row */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', borderTop: '1px solid var(--border)',
              background: 'var(--bg)',
            }}>
              <span style={{
                fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-3)',
                userSelect: 'none',
              }}>
                {hint}
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  onClick={speechSupported ? toggleDictation : undefined}
                  title={speechSupported ? undefined : 'Dictation not supported in this browser — use Chrome or Safari'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    background: listening ? 'rgba(220,38,38,0.08)' : 'none',
                    border: '1px solid',
                    borderColor: listening ? '#DC2626' : 'var(--border)',
                    borderRadius: 6, padding: '5px 10px',
                    color: listening ? '#DC2626' : speechSupported ? 'var(--text-2)' : 'var(--text-3)',
                    fontSize: 13, fontFamily: 'var(--font-body)',
                    cursor: speechSupported ? 'pointer' : 'default',
                    opacity: speechSupported ? 1 : 0.5,
                    transition: 'all .15s',
                  }}
                >
                  <MicIcon active={listening} />
                  {listening ? 'Listening…' : 'Dictate'}
                </button>

                <button
                  onClick={onContinue}
                  disabled={!canContinue}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 16px', borderRadius: 6,
                    background: 'var(--text)',
                    color: 'var(--bg)',
                    opacity: canContinue ? 1 : 0.35,
                    border: 'none', fontSize: 14,
                    fontFamily: 'var(--font-body)', fontWeight: 500,
                    cursor: canContinue ? 'pointer' : 'default',
                    transition: 'opacity .15s',
                  }}
                >
                  {loading ? 'Working…' : 'Continue'}
                  {!loading && (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <p style={{ marginTop: 12, color: 'var(--error)', fontSize: 13, fontFamily: 'var(--font-body)' }}>
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Review screen helpers ────────────────────────────────────────────────────

function InlineEdit({ value, onSave, style }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef();

  function start() {
    setDraft(value);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function save() {
    const trimmed = draft.trim();
    if (trimmed) onSave(trimmed);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        style={{
          ...style,
          background: 'var(--surface-raised)',
          border: '1px solid var(--accent-axon, #4A90D9)',
          borderRadius: 4,
          padding: '1px 6px',
          outline: 'none',
          fontSize: 'inherit',
          fontFamily: 'inherit',
          color: 'inherit',
          width: '100%',
          boxSizing: 'border-box',
        }}
      />
    );
  }

  return (
    <span
      onClick={start}
      title="Click to edit"
      style={{ ...style, cursor: 'text', borderBottom: '1px dotted var(--border)' }}
    >
      {value}
    </span>
  );
}

function DeleteBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      title="Remove"
      style={{
        background: 'none',
        border: '1px solid var(--border)',
        borderRadius: 4,
        cursor: 'pointer',
        color: 'var(--text-2)',
        fontSize: 13,
        padding: '1px 6px',
        lineHeight: 1.2,
        flexShrink: 0,
        fontFamily: 'var(--font-body)',
      }}
    >
      ×
    </button>
  );
}

function AddBtn({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: '1px dashed var(--border)',
        borderRadius: 6,
        cursor: 'pointer',
        color: 'var(--text-2)',
        fontSize: 12,
        fontFamily: 'var(--font-body)',
        padding: '5px 12px',
        lineHeight: 1.2,
      }}
    >
      {children}
    </button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Onboarding({ onComplete, onLogout }) {
  const [step, setStep] = useState('loading');
  const [brainDump, setBrainDump] = useState('');
  const [baselineQuestion, setBaselineQuestion] = useState('');
  const [baselineAnswer, setBaselineAnswer] = useState('');
  const [confirmedMap, setConfirmedMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Resume check ──────────────────────────────────────────────────────────

  useEffect(() => {
    fetch('/api/onboarding/status')
      .then(r => r.json())
      .then(data => {
        if (data.step === 'follow_up' && data.brain_dump) {
          setBrainDump(data.brain_dump);
          setBaselineQuestion(data.baseline_question || '');
          setStep('follow_up');
        } else if (data.step === 'extracted' && data.extracted_map) {
          setBrainDump(data.brain_dump || '');
          setBaselineQuestion(data.baseline_question || '');
          setBaselineAnswer(data.baseline_answer || '');
          setConfirmedMap(deepCopy(data.extracted_map));
          setStep('review');
        } else {
          setStep('brain_dump');
        }
      })
      .catch(() => setStep('brain_dump'));
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────

  async function handleFollowup() {
    if (brainDump.trim().length < 20) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch('/api/onboarding/followup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brain_dump: brainDump }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Something went wrong');
      setBaselineQuestion(data.baseline_question);
      setStep('follow_up');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleExtract() {
    if (baselineAnswer.trim().length < 5) return;
    setLoading(true);
    setError('');
    try {
      const r = await fetch('/api/onboarding/extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ baseline_answer: baselineAnswer }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Something went wrong');
      setConfirmedMap(deepCopy(data.extracted_map));
      setStep('review');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    if (!confirmedMap || !confirmedMap.workstreams.length) return;
    setLoading(true);
    setError('');
    setStep('confirming');
    try {
      localStorage.removeItem('b360_hint_ws');
      localStorage.removeItem('b360_hint_chat');
      const r = await fetch('/api/onboarding/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmed_map: confirmedMap }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Something went wrong');
      await onComplete();
    } catch (err) {
      setError(err.message);
      setStep('review');
      setLoading(false);
    }
  }

  // ── Map editing ───────────────────────────────────────────────────────────

  function updateMap(fn) {
    setConfirmedMap(prev => {
      const next = deepCopy(prev);
      fn(next);
      return next;
    });
  }

  function deleteTask(wsIdx, taskIdx) {
    updateMap(m => { m.workstreams[wsIdx].tasks.splice(taskIdx, 1); });
  }

  function editTaskText(wsIdx, taskIdx, text) {
    updateMap(m => { m.workstreams[wsIdx].tasks[taskIdx].text = text; });
  }

  function deleteContext(wsIdx, ctxIdx) {
    updateMap(m => { m.workstreams[wsIdx].context.splice(ctxIdx, 1); });
  }

  function deleteWorkstream(wsIdx) {
    updateMap(m => { m.workstreams.splice(wsIdx, 1); });
  }

  function editWorkstreamName(wsIdx, name) {
    updateMap(m => { m.workstreams[wsIdx].name = name; });
  }

  function editBaseline(text) {
    updateMap(m => { m.good_week_baseline = text; });
  }

  function addWorkstream() {
    updateMap(m => {
      if (!Array.isArray(m.workstreams)) m.workstreams = [];
      m.workstreams.push({ name: 'New workstream', tasks: [], context: [] });
    });
  }

  function addTask(wsIdx) {
    updateMap(m => {
      if (!m.workstreams[wsIdx].tasks) m.workstreams[wsIdx].tasks = [];
      m.workstreams[wsIdx].tasks.push({ text: 'New task', inferred: false, confidence: 'high', source: '' });
    });
  }

  // ── Screens ───────────────────────────────────────────────────────────────

  if (step === 'loading') {
    return (
      <Page>
        <p style={{ color: 'var(--text-3)', fontFamily: 'var(--font-body)' }}>Loading…</p>
      </Page>
    );
  }

  if (step === 'confirming') {
    return (
      <Page onLogout={onLogout}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--text)', marginBottom: 12 }}>
            Building your briefing…
          </p>
          <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-2)', fontSize: 14 }}>
            Setting up your workstreams and generating your first read.
          </p>
        </div>
      </Page>
    );
  }

  if (step === 'brain_dump') {
    return (
      <BrainDumpScreen
        brainDump={brainDump}
        setBrainDump={setBrainDump}
        loading={loading}
        error={error}
        onContinue={handleFollowup}
        onLogout={onLogout}
      />
    );
  }

  if (step === 'follow_up') {
    return (
      <Page onLogout={onLogout}>
        <div style={{ marginBottom: 28 }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-3)', margin: '0 0 20px', letterSpacing: '0.02em' }}>
            Step 2 of 3 · this week's target
          </p>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 400, lineHeight: 1.25, margin: '0 0 10px', color: 'var(--text)' }}>
            One question.
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-2)', margin: 0, lineHeight: 1.6 }}>
            Your answer anchors this week — not a general philosophy, but what specifically needs to be true by the end of <strong>this</strong> week for it to feel like it counted. Answer it, and we'll build your map.
          </p>
        </div>

        <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--surface)' }}>
          {/* Question */}
          <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)' }}>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-3)', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Bearing360 asks
            </p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
              {baselineQuestion}
            </p>
          </div>

          {/* Answer */}
          <div>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-3)', margin: '14px 20px 6px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              Your answer
            </p>
            <textarea
              autoFocus
              value={baselineAnswer}
              onChange={e => setBaselineAnswer(e.target.value)}
              placeholder="By Sunday evening I want to…"
              rows={4}
              style={{
                display: 'block', width: '100%', boxSizing: 'border-box',
                padding: '0 20px 16px',
                border: 'none', outline: 'none', resize: 'none',
                fontFamily: 'var(--font-body)', fontSize: 15, lineHeight: 1.6,
                color: 'var(--text)', background: 'transparent',
              }}
            />
          </div>
        </div>

        <ErrorMsg msg={error} />
        <PrimaryButton onClick={handleExtract} disabled={baselineAnswer.trim().length < 5} loading={loading}>
          That's the target →
        </PrimaryButton>
      </Page>
    );
  }

  if (step === 'review') {
    const workstreams = confirmedMap?.workstreams || [];
    const baseline = confirmedMap?.good_week_baseline || '';
    return (
      <Page onLogout={onLogout}>
        <div style={{ marginBottom: 32 }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-3)', margin: '0 0 8px', letterSpacing: '0.02em' }}>
            Step 3 of 3 · confirm your map
          </p>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--text-2)' }}>
            Bearing360
          </span>
        </div>
        <H1>Here's what I heard.</H1>
        <Sub>
          Edit anything that's off — rename workstreams, remove tasks or context. Then confirm your map.
        </Sub>

        {/* Good week baseline */}
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: 8,
          background: 'var(--surface)',
          marginBottom: 28,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-3)',
              margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>
              Good week looks like
            </p>
          </div>
          <textarea
            value={baseline}
            onChange={e => editBaseline(e.target.value)}
            rows={2}
            style={{
              display: 'block', width: '100%', boxSizing: 'border-box',
              padding: '12px 16px',
              border: 'none', outline: 'none', resize: 'none',
              fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.6,
              color: 'var(--text)', background: 'transparent',
            }}
          />
        </div>

        {workstreams.length === 0 && (
          <p style={{ color: 'var(--text-3)', fontFamily: 'var(--font-body)', fontStyle: 'italic', marginBottom: 24 }}>
            All workstreams removed. Go back to re-extract.
          </p>
        )}

        {workstreams.map((ws, wsIdx) => {
          const color = CARD_COLORS[wsIdx % CARD_COLORS.length];
          return (
            <div key={wsIdx} style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderLeft: `4px solid ${color}`,
              borderRadius: 8,
              padding: '16px 18px',
              marginBottom: 16,
            }}>
              {/* Workstream header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <InlineEdit
                  value={ws.name}
                  onSave={name => editWorkstreamName(wsIdx, name)}
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 17,
                    fontWeight: 400,
                    color: 'var(--text)',
                  }}
                />
                <button
                  onClick={() => deleteWorkstream(wsIdx)}
                  title="Remove workstream"
                  style={{
                    background: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 5,
                    cursor: 'pointer',
                    color: 'var(--text-2)',
                    fontSize: 11,
                    fontFamily: 'var(--font-body)',
                    padding: '3px 8px',
                    marginLeft: 12,
                    flexShrink: 0,
                  }}
                >
                  remove
                </button>
              </div>

              {/* Tasks */}
              {ws.tasks && ws.tasks.length > 0 && (
                <div style={{ marginBottom: ws.context?.length ? 12 : 0 }}>
                  <p style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-body)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Tasks
                  </p>
                  {ws.tasks.map((task, taskIdx) => (
                    <div key={taskIdx} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 6,
                      marginBottom: 8,
                      paddingLeft: 4,
                    }}>
                      <span style={{ color: color, marginTop: 2, fontSize: 12, flexShrink: 0 }}>●</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                          <InlineEdit
                            value={task.text}
                            onSave={text => editTaskText(wsIdx, taskIdx, text)}
                            style={{
                              fontFamily: 'var(--font-body)',
                              fontSize: 14,
                              color: 'var(--text)',
                              flex: 1,
                            }}
                          />
                          <DeleteBtn onClick={() => deleteTask(wsIdx, taskIdx)} />
                        </div>
                        {task.source && (
                          <p style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-body)', margin: '2px 0 0', fontStyle: 'italic' }}>
                            "{task.source}"
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Context */}
              {ws.context && ws.context.length > 0 && (
                <div>
                  <p style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-body)', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Context
                  </p>
                  {ws.context.map((ctx, ctxIdx) => (
                    <div key={ctxIdx} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 6,
                      marginBottom: 8,
                      paddingLeft: 4,
                    }}>
                      <span style={{ color: 'var(--text-3)', marginTop: 2, fontSize: 12, flexShrink: 0 }}>◦</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                          <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-2)', flex: 1 }}>
                            {ctx.text}
                          </span>
                          <span style={{
                            fontSize: 10,
                            color: 'var(--text-3)',
                            border: '1px solid var(--border)',
                            borderRadius: 4,
                            padding: '1px 5px',
                            fontFamily: 'var(--font-body)',
                            flexShrink: 0,
                            marginTop: 2,
                            whiteSpace: 'nowrap',
                          }}>
                            {typeLabel(ctx.type)}
                          </span>
                          <DeleteBtn onClick={() => deleteContext(wsIdx, ctxIdx)} />
                        </div>
                        {ctx.source && (
                          <p style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-body)', margin: '2px 0 0', fontStyle: 'italic' }}>
                            "{ctx.source}"
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add task to this workstream */}
              <div style={{ marginTop: 4 }}>
                <AddBtn onClick={() => addTask(wsIdx)}>+ task</AddBtn>
              </div>
            </div>
          );
        })}

        {/* Add a new workstream */}
        <div style={{ marginBottom: 28 }}>
          <AddBtn onClick={addWorkstream}>+ add workstream</AddBtn>
        </div>

        <ErrorMsg msg={error} />
        <PrimaryButton
          onClick={handleConfirm}
          disabled={workstreams.length === 0}
          loading={loading}
        >
          This is my world →
        </PrimaryButton>

        <button
          onClick={() => { setStep('brain_dump'); setError(''); }}
          style={{
            display: 'block',
            marginTop: 12,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-3)',
            fontSize: 13,
            fontFamily: 'var(--font-body)',
            padding: 0,
          }}
        >
          ← Start over
        </button>
      </Page>
    );
  }

  return null;
}
